import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.requiresSellingPlan === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // ECS: quick add never navigates to the PDP — upstream sent every selling-plan product
    // there, which read as a broken add-to-cart. Products that merely OFFER a plan open the
    // quick-add modal and add a one-time purchase. The one exception is a product that
    // REQUIRES a plan: it cannot be bought one-time, and the modal strips the plan selector,
    // so it keeps the hand-off to the PDP where the selector lives.
    const currentUrl = this.productPageUrl;

    if (this.dataset.requiresSellingPlan === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    this.#openQuickAddModal();
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // ECS: the quick-add modal adds a one-time purchase, like the card's size bar. The PDP's
    // purchase-delivery block (blocks/purchase-delivery.liquid) arrives in the fetched markup,
    // but its module never runs inside the modal, leaving inert One-time/Subscribe controls.
    // Removing it (and the block's module script tag) keeps the subscription choice on the
    // PDP, per the approved design.
    for (const purchaseDelivery of productGrid.querySelectorAll(
      'purchase-delivery, .purchase-delivery, script[src*="purchase-delivery"]'
    )) {
      purchaseDelivery.remove();
    }

    // ECS: the price sits directly under the title in every popup. The PDP template puts it
    // in the "Total" row above the buy box, which the popup hid behind a long variant or
    // option list; the mobile layout below already builds a title + price header.
    if (!isMobileBreakpoint()) {
      const title = productGrid.querySelector('.view-product-title');
      const priceBlock = productGrid.querySelector('product-price')?.closest('.group-block');
      if (title && priceBlock && !priceBlock.contains(title)) title.after(priceBlock);
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    // Sync the view-event-payload attribute and morph children into the modal's product-component
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    this.#mountLineItemProperties(modalContent);
  }

  /**
   * ECS: mounts the Infinite Options app's fields inside the modal's product form for
   * products the app targets (data-line-item-properties, set at runtime by
   * assets/quick-add-line-item-properties.js from the app's own config).
   * On its own the app only renders on product pages (it reads the handle from the URL),
   * but its public init(handle, containerId) renders into any
   * [data-infinite-options-container-id] element and binds the fields to that element's
   * form, so the modal's Add to bag submits them as line item properties. Verified against
   * the live app bundle. Without the app nothing happens and the modal adds as before.
   * @param {Element} modalContent - The modal's product component
   */
  #mountLineItemProperties(modalContent) {
    if (this.dataset.lineItemProperties !== 'true') return;

    const handle = this.dataset.productHandle;
    const app = window.Shoppad?.apps?.infiniteoptions;
    if (!handle || typeof app?.init !== 'function') return;

    const form = modalContent.querySelector('product-form-component form');
    const formId = form?.getAttribute('id');
    if (!form || !formId) return;

    const containerId = `quick-add-options-${this.dataset.productId}`;
    modalContent.querySelector(`[data-infinite-options-container-id="${containerId}"]`)?.remove();
    modalContent.querySelector('.quick-add-modal__option-pills')?.remove();

    const container = document.createElement('div');
    container.className = 'quick-add-modal__line-item-properties';
    container.dataset.infiniteOptions = handle;
    container.dataset.infiniteOptionsContainerId = containerId;
    // The fields sit where a variant picker would — under the title and price, above the
    // buy box — so they read as the product's options. That is outside the form element,
    // so the container carries the form's id: the app copies it onto every field it renders
    // (the `form` attribute), which keeps them part of the popup's add-to-cart submit.
    container.setAttribute('form', formId);

    const variantPicker = modalContent.querySelector('variant-picker');
    const price = modalContent.querySelector('product-price');
    const priceBlock = price?.closest('.group-block');

    if (variantPicker) {
      (variantPicker.closest('.group-block') ?? variantPicker).after(container);
    } else if (price?.closest('.product-header')) {
      // Mobile layout: the header holds title and price; options follow it like a picker would.
      price.closest('.product-header').after(container);
    } else if (priceBlock) {
      priceBlock.after(container);
    } else {
      const addToCart = form.querySelector('add-to-cart-component') ?? form.querySelector('[type="submit"]');
      if (!addToCart) return;
      addToCart.before(container);
    }

    this.#renderOptionPills(container, form);

    // The app declines to start while another of its inits is in flight (e.g. the PDP's
    // own render when quick add is used from a product page); give it a moment and retry.
    const start = (attempt = 0) => {
      if (app._running && attempt < 5) {
        setTimeout(() => start(attempt + 1), 200);
        return;
      }
      app.init(handle, containerId);
    };
    start();
  }

  /**
   * ECS: presents the app's <select> fields as the theme's variant-picker pills, so a
   * product whose fragrance is an app option looks exactly like one whose fragrance is a
   * Shopify variant. The selects stay in the DOM (hidden) as the source of truth the app
   * validates and the form submits; each pill group mirrors one select and writes back to
   * it. Add to bag is held until every required group has a choice, with the message the
   * theme owns, since a hidden select cannot show the browser's own prompt.
   * @param {HTMLElement} container - The app's container
   * @param {HTMLFormElement} form - The popup's product form
   */
  #renderOptionPills(container, form) {
    const requiredText = this.dataset.optionRequiredText || '';
    let pills = null;

    const legendText = (select) => {
      const label = container.querySelector(`label[for="${select.id}"]`);
      let text = (label?.textContent ?? select.name).trim().replace(/:\s*$/, '');
      if (text && text === text.toUpperCase()) text = text.charAt(0) + text.slice(1).toLowerCase();
      return text;
    };

    const build = () => {
      const selects = /** @type {HTMLSelectElement[]} */ ([...container.querySelectorAll('select')]);
      const signature = selects.map((select) => `${select.name}:${select.options.length}`).join('|');
      if (!selects.length || pills?.dataset.signature === signature) return;

      pills?.remove();
      pills = document.createElement('div');
      pills.className = 'variant-picker variant-picker--left quick-add-modal__option-pills';
      pills.dataset.signature = signature;

      for (const [index, select] of selects.entries()) {
        const fieldset = document.createElement('fieldset');
        fieldset.className = 'variant-option variant-option--buttons variant-option--equal-width-buttons';
        fieldset.dataset.optionIndex = String(index);

        const legend = document.createElement('legend');
        legend.textContent = legendText(select);
        fieldset.append(legend);

        const name = `${container.dataset.infiniteOptionsContainerId}-${index}`;
        for (const option of select.options) {
          if (!option.value) continue;
          const label = document.createElement('label');
          label.className = 'variant-option__button-label';

          const input = document.createElement('input');
          input.type = 'radio';
          input.name = name;
          input.value = option.value;
          input.setAttribute('aria-label', option.textContent?.trim() ?? option.value);
          input.dataset.optionAvailable = 'true';
          input.checked = select.value === option.value;
          input.dataset.currentChecked = String(input.checked);
          input.addEventListener('change', () => {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            for (const radio of fieldset.querySelectorAll('input')) {
              radio.dataset.currentChecked = String(radio.checked);
            }
            fieldset.removeAttribute('aria-invalid');
            fieldset.querySelector('.quick-add-modal__option-error')?.remove();
          });

          const pill = document.createElement('span');
          pill.className = 'variant-option__button-label__pill';
          const text = document.createElement('span');
          text.className = 'variant-option__button-label__text';
          text.textContent = option.textContent?.trim() ?? option.value;

          label.append(input, pill, text);
          fieldset.append(label);
        }
        pills.append(fieldset);
      }

      container.after(pills);
      container.classList.add('quick-add-modal__line-item-properties--as-pills');
    };

    new MutationObserver(build).observe(container, { childList: true, subtree: true });
    build();

    form.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element) || !event.target.closest('[type="submit"]')) return;
        if (!pills) return;

        const selects = /** @type {HTMLSelectElement[]} */ ([...container.querySelectorAll('select')]);
        const missing = selects.map((select, index) => (select.required && !select.value ? index : -1)).filter((i) => i >= 0);
        if (!missing.length) return;

        // Stops the native submit; the theme's own click handler already sees an invalid form.
        event.preventDefault();
        for (const index of missing) {
          const fieldset = pills.querySelector(`[data-option-index="${index}"]`);
          if (!fieldset) continue;
          fieldset.setAttribute('aria-invalid', 'true');
          if (!fieldset.querySelector('.quick-add-modal__option-error') && requiredText) {
            const error = document.createElement('p');
            error.className = 'quick-add-modal__option-error';
            error.setAttribute('role', 'alert');
            error.textContent = requiredText;
            // Under the legend, where it is read before the (possibly long) list of pills.
            fieldset.querySelector('legend')?.after(error);
          }
        }
        pills.querySelector(`[data-option-index="${missing[0]}"] input`)?.focus();
      },
      true
    );
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
